import React, { FC } from 'react';
import { Redirect } from 'react-router';
import { isRefrigerantTrackingEnabled } from '../../utils/FeatureFlagUtils';

/**
 * Route guard for the refrigerant-tracking pages.
 *
 * Hiding the tabs that link to these pages is not the same as gating them: the URLs stay typeable
 * and bookmarkable, and a servicing record is exactly the sort of page somebody keeps a link to. So
 * the routes are guarded here as well, and a flag-off visit lands on the page the tab sits beside
 * rather than on a blank shell.
 *
 * The gate lives in a component rather than in the `routes` array itself because that array is a
 * module-level constant, evaluated on import — before posthog has loaded its flags, when
 * `isFeatureEnabled` still answers `undefined` for everything. Reading the flag during render is
 * what makes the answer the real one.
 *
 * `getFallbackPath` receives the route props, so a per-entity page can send the visitor back to that
 * same entity.
 */
export const withRefrigerantTrackingFlag = (
	WrappedComponent: any,
	getFallbackPath: (routeProps: any) => string
): FC<any> => {
	const RefrigerantTrackingRoute: FC<any> = (props) =>
		isRefrigerantTrackingEnabled() ? (
			<WrappedComponent {...props} />
		) : (
			<Redirect to={getFallbackPath(props)} />
		);

	const wrappedName =
		WrappedComponent.displayName || WrappedComponent.name || 'RefrigerantTrackingPage';
	RefrigerantTrackingRoute.displayName = `withRefrigerantTrackingFlag(${wrappedName})`;

	return RefrigerantTrackingRoute;
};

export default withRefrigerantTrackingFlag;
